import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  Radio, Users, Heart, Share2, MessageCircle, 
  Lock, Loader2, AlertCircle, ExternalLink, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LiveStream = () => {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeader } = useAuth();
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStream();
    // Increment viewer count
    incrementViewerCount();
  }, [streamId]);

  const fetchStream = async () => {
    try {
      const response = await axios.get(`${API}/streams/${streamId}`);
      setStream(response.data);
    } catch (error) {
      setError('Stream not found or has ended');
    } finally {
      setLoading(false);
    }
  };

  const incrementViewerCount = async () => {
    try {
      await axios.post(`${API}/streams/${streamId}/view`);
    } catch (error) {
      // Non-critical: viewer count increment can fail silently
      console.debug('Viewer count increment failed:', error.message);
    }
  };

  const getEmbedUrl = (url, platform) => {
    if (!url) return null;
    
    // YouTube
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : null;
    }
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : null;
    }
    
    // Twitch
    if (url.includes('twitch.tv/')) {
      const channel = url.split('twitch.tv/')[1]?.split('/')[0];
      return channel ? `https://player.twitch.tv/?channel=${channel}&parent=${window.location.hostname}` : null;
    }
    
    // Return as-is for custom embeds
    return url;
  };

  const shareStream = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !stream) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl text-white mb-2">Stream Unavailable</h2>
          <p className="text-gray-400 mb-6">{error || 'This stream may have ended'}</p>
          <Button onClick={() => navigate('/live')} className="bg-white text-black hover:bg-gray-200">
            Browse Live Streams
          </Button>
        </div>
      </div>
    );
  }

  // Premium check
  if (stream.is_premium && (!isAuthenticated || !user?.is_premium)) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Lock className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl text-white mb-2">Premium Stream</h2>
          <p className="text-gray-400 mb-6">This live stream is for premium subscribers only</p>
          <Button onClick={() => navigate('/subscription')} className="bg-amber-500 text-black hover:bg-amber-400">
            Upgrade to Premium
          </Button>
        </div>
      </div>
    );
  }

  const embedUrl = getEmbedUrl(stream.stream_url, stream.platform);

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 lg:px-16 py-8 bg-[#050505]" data-testid="live-stream-page">
      <div className="max-w-6xl mx-auto">
        {/* Live Badge */}
        <div className="flex items-center gap-3 mb-4">
          <span className="flex items-center gap-2 px-3 py-1 bg-red-500 text-white text-sm font-medium rounded-full animate-pulse">
            <Radio className="w-4 h-4" />
            LIVE
          </span>
          <span className="flex items-center gap-2 text-gray-400 text-sm">
            <Users className="w-4 h-4" />
            {stream.viewer_count || 0} watching
          </span>
        </div>

        {/* Stream Player */}
        <div className="bg-black rounded-lg overflow-hidden mb-6">
          <div className="aspect-video">
            {embedUrl ? (
              <iframe
                src={embedUrl}
                className="w-full h-full"
                frameBorder="0"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                title={stream.title}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-[#121212]">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">Unable to embed stream</p>
                  <a 
                    href={stream.stream_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-4 text-blue-400 hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Watch on {stream.platform}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stream Info */}
        <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2" data-testid="stream-title">
                {stream.title}
              </h1>
              
              <Link 
                to={`/creator/${stream.creator_id}`}
                className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors mb-4"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <span className="font-medium">{stream.creator_name}</span>
              </Link>

              {stream.description && (
                <p className="text-gray-400 text-sm">{stream.description}</p>
              )}
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={shareStream}
                className="border-white/20"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <a 
                href={stream.stream_url} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="border-white/20">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Source
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Stream ended notice */}
        {stream.status !== 'live' && (
          <div className="mt-6 bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 text-center">
            <p className="text-amber-400">This stream has ended</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveStream;
